import { useContext, useEffect } from 'react'
import deepEqual from 'deep-equal'
import { AppContext, AppContextType } from '../AppContext'
import useArrayDiff from '../hooks/useArrayDiff'
import useDebounce from '../hooks/useDebounce'
import { media_selection_prev_preference } from '../services/Constants'
import { persistent } from '../services/LocalStorage'
import { logger } from '../services/Logger'
import {
    desktopMediaSourceToStream,
    getAudioTrack,
    getVideoStreamFromId,
    MediaDeviceError,
} from '../services/MediaDevices'
import {
    AudioInputError,
    AudioInputSelection,
    AudioInputStreamInfo,
    AudioTrackWithInfo,
    MediaSelectionPrevPreference,
    UserContext,
    UserContextType,
} from '../UserContext'
import { AudioEngineError, getAudioEngineInstance, AudioEngineLocalStreamStats } from '../core/AudioEngine'
import useObsController from '../services/useObsController'
import { useDesktopSourceResolver } from '../services/useDesktopSourceResolver'
import { FeatureFlags } from '../services/FeatureFlags'
import useUserContextRef from '../services/useUserContextRef'
import { isDefinedDeviceId } from '../Logic'
import useGlobalOutputDeviceAction from '../actions/useGlobalOutputDeviceActions'
import useMediaSourceViews from '../actions/useMediaSourceViews'
import useMediaSelectionActions from '../actions/useMediaSelectionActions'
import useAppContextRef from '../services/useAppContextRef'
import useAudioSettingsStoreActions from '../core/useAudioSettingsStoreActions'
import FakeMediaStreamTrack from '../core/AudioEngine/FakeMediaStreamTrack'
import useAudioEngineActions from '../actions/useAudioEngineActions'
import { useTranslation } from 'react-i18next'
import { createLocalAudioTrack, LocalTrack, LocalVideoTrack } from 'livekit-client'
import { RoomContext, RoomContextType } from '../RoomContext'
import useAudioEngineReloadedDevicesCache from '../services/useAudioEngineReloadedDevicesCache'
import useRoomContextRef from '../services/useRoomContextRef'
import { speakerDetectionManager } from '../services/SpeakerDetectionManager'
import useVirtualBackgroundActions from '@/services/useVirtualBackgroundActions'

const log = logger.createChildLogger({ name: 'MediaSelectionResolver' })

/**
 * Detects changes to UserContext's mediaSelection.desktop, video, and audioInputs[], then resolves
 * the changed mediaSelection to media streams, modifying UserContext's mediaStreams accordingly.
 *
 * For audioInputs[], debouncing is applied.
 *
 * When mediaSelection is deselected, the corresponding mediaSelection's track is also stopped properly.
 */
function MediaSelectionResolver() {
    const { t } = useTranslation()

    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { revertToPreviousGlobalAudioOutputDevice } = useGlobalOutputDeviceAction()

    const { toggleAudioDeviceSelection, reloadAudioDeviceSelection } = useMediaSelectionActions()

    const { setAudioInputSettings } = useAudioSettingsStoreActions()

    const { audioInputsClassifiedForUi } = useMediaSourceViews()

    const { shouldReloadDeviceOnUnresponsive, saveReloadUnresponsiveDeviceInfo } = useAudioEngineReloadedDevicesCache()

    const audioInputsSelection_debounced = useDebounce<AudioInputSelection[]>(state.mediaSelection.audioInputs, 1000)

    // Since `audioInputsSelection_debounced` is an array, we filter for added/removed elements.
    // Recall that `audioInputsSelection_debounced_diff` is a ref, so is not reactive and is readonly.
    const audioInputsSelection_debounced_diff = useArrayDiff<AudioInputSelection>(
        audioInputsSelection_debounced,
        (a, b) => deepEqual(a, b)
    )

    const { getDesktopSourceDimensions } = useDesktopSourceResolver()

    const ObsController = useObsController({ instanceName: 'screenshare' })

    const { setVirtualBackground } = useVirtualBackgroundActions()

    // video selection changed
    useEffect(() => {
        ;(async function () {
            // If previous video stream exists, stop it.

            dispatch({
                type: 'update_media_streams',
                value: { videoLoading: true },
            })

            if (state.mediaStreams.video instanceof LocalVideoTrack) {
                state.mediaStreams.video.stop()
            }

            const videoSource =
                state.mediaSources.video.filter((s) => s.deviceId === state.mediaSelection.video)[0] || null // used for logging purposes
            const options = {
                width: appState.appSettings.videoOptions?.optionWidth || undefined,
                frameRate: appState.appSettings.videoOptions?.optionFramerate || undefined,
                aspectRatio: appState.appSettings.videoOptions?.optionAspectRatio || undefined,
            }

            const video: LocalVideoTrack | MediaDeviceError | null = await getVideoStreamFromId(
                state.mediaSelection.video,
                options
            )

            if (video != null) {
                roomDispatch({
                    type: 'update_room_context',
                    value: { myVideoMuteState: false },
                })
            }

            const currentProcessor = state.mediaStreams.videoProcessor

            // Register 'ended' event for the video and unselect if the video is ended.
            if (video instanceof LocalVideoTrack) {
                video.addListener('ended', () => {
                    dispatch({
                        type: 'update_media_selection',
                        value: {
                            video: '_undef',
                        },
                    })

                    try {
                        video.stop()
                    } catch (e) {}

                    logger.event({
                        page: 'MediaSelectionResolver',
                        action: 'video_stream_end',
                        data: {
                            video: videoSource,
                            videoOptions: appState.appSettings.videoOptions,
                        },
                    })
                })
                if (currentProcessor) {
                    await currentProcessor.processor.destroy()
                    // await video.setProcessor(currentProcessor.processor)
                }

                const background = state.mediaSelection.selectedVirtualBackground
                await setVirtualBackground(background, video)
            } else if (video instanceof MediaDeviceError) {
                const e = video
                log.error('resolveVideoSelection', 'failed to access device:', {
                    deviceId: state.mediaSelection.video,
                    options: options,
                    name: e.name,
                    message: e.message,
                    mediaType: e.mediaType,
                })
            }

            dispatch({
                type: 'update_media_streams',
                value: { video: video, videoLoading: false },
            })

            logger.event({
                page: 'MediaSelectionResolver',
                action: 'video_selection',
                data: {
                    video: videoSource,
                    videoOptions: appState.appSettings.videoOptions,
                },
            })
        })()
    }, [state.mediaSelection.video, appState.appSettings.videoOptions])

    // desktop selection changed
    useEffect(() => {
        ;(async function () {
            if (state.mediaSelection.desktop === '__browser_desktop') {
                // is desktop id selected from a browser session; mediaStream for this desktop
                // is handled at openBrowserDesktopStreamSelector() since it directly uses the
                // browser's built in `navigator.mediaDevices.getDisplayMedia()` to get the desktop stream.
                return
            }

            // If previous desktop stream exists, stop it.
            if (state.mediaStreams.desktop instanceof MediaStream) {
                state.mediaStreams.desktop.getTracks().forEach((track) => {
                    console.log(`desktop: stopping track %O`, track)
                    track.stop()
                })
            }

            // stop sharing Muse Audio Share if Muse Audio Share was toggled on due to
            // checking the "Share computer audio" checkbox in the ScreenShareSelectionPage.
            if (!isDefinedDeviceId(state.mediaSelection.desktop)) {
                const { undoGlobalOutputDeviceWhenScreenShareEnds } = userStateRef.current.mediaSelection
                log.verbose(
                    'desktopSelectionChanged',
                    `screen share ended: userState.mediaSelection.undoGlobalOutputDeviceWhenScreenShareEnds=` +
                        `${undoGlobalOutputDeviceWhenScreenShareEnds}.`
                )
                if (undoGlobalOutputDeviceWhenScreenShareEnds) {
                    const systemAudioInput = audioInputsClassifiedForUi.system[0] || null
                    const isChecked = state.mediaSelection.audioInputs
                        .map((i) => i.deviceId)
                        .includes(systemAudioInput.deviceId)
                    if (isChecked) {
                        toggleAudioDeviceSelection(systemAudioInput)
                    }

                    revertToPreviousGlobalAudioOutputDevice()
                }
            }

            let desktopSource =
                state.mediaSources.desktop.filter((s) => s.id === state.mediaSelection.desktop)[0] || null

            let desktop: MediaStream | MediaDeviceError | null = null
            let isObsAvailable = false

            if (FeatureFlags.isEnabled('obs-screen-share')) {
                const virtualCamera = state.mediaSources.virtualCamera

                isObsAvailable = await ObsController.isAvailable()

                const requestedDesktopDimensions = await getDesktopSourceDimensions(
                    state.mediaSelection.desktop,
                    appState.appSettings.screenShareDesktopVideoOptions || {}
                )

                if (desktopSource && desktopSource.screenIndex != null && virtualCamera && isObsAvailable) {
                    const obsConfig = ObsController.makeConfigFromDesktopSource(desktopSource, {
                        screenWidth: requestedDesktopDimensions?.width || undefined,
                        frameRate: appState.appSettings.screenShareDesktopVideoOptions?.optionFramerate || 30,
                    })
                    if (obsConfig) {
                        // Show loading indicator before `ObsController.launchProcess` promise returns.
                        dispatch({
                            type: 'update_media_streams',
                            value: { desktop: new MediaDeviceError('screen', { name: 'loading' }) },
                        })

                        const launchResult = await ObsController.launchProcess({
                            runBatch: 'screen-sharing',
                            configOverride: obsConfig,
                        })

                        if (launchResult.code === 'obs_launch_success' && launchResult.pid != null) {
                            const desktopTrack = await getVideoStreamFromId(virtualCamera.deviceId, {
                                width: requestedDesktopDimensions?.width || undefined,
                                height: requestedDesktopDimensions?.height || undefined,
                            })
                            desktop =
                                desktopTrack instanceof LocalVideoTrack && desktopTrack.mediaStream
                                    ? desktopTrack.mediaStream
                                    : null
                        }
                    }
                }
            }

            if (!(desktop instanceof MediaStream)) {
                desktop = await desktopMediaSourceToStream(
                    desktopSource,
                    appState.appSettings.screenShareDesktopVideoOptions || {}
                )
                if (isObsAvailable) {
                    ObsController.killProcess()
                }
            }

            if (desktop instanceof MediaStream) {
                let _ended = false
                desktop.getVideoTracks().forEach((vtrack) => {
                    vtrack.addEventListener('ended', () => {
                        if (_ended) {
                            return
                        }
                        _ended = true

                        try {
                            vtrack.stop()
                            if (desktop instanceof MediaStream) {
                                desktop.stop()
                            }
                        } catch (e) {}

                        console.log(`desktop (screenshare) track id=${vtrack.id} onended. Updating mediaSelection.`)
                        dispatch({
                            type: 'update_media_selection',
                            value: {
                                desktop: '_undef',
                            },
                        })
                        dispatch({
                            type: 'update_media_streams',
                            value: { desktop: null },
                        })

                        let data = {
                            id: desktopSource?.id,
                        }
                        logger.event({ page: 'MediaSelectionResolver', action: 'desktop_stream_end', data })
                    })
                })
            } else if (desktop instanceof MediaDeviceError) {
                const e = desktop
                log.error('resolveDesktopSelection', 'failed to access device:', {
                    deviceId: desktopSource?.id,
                    options: appState.appSettings.screenShareDesktopVideoOptions || {},
                    name: e.name,
                    message: e.message,
                    mediaType: e.mediaType,
                })
            }

            dispatch({
                type: 'update_media_streams',
                value: { desktop: desktop },
            })

            if (desktop instanceof MediaStream && desktopSource) {
                let data = {
                    desktopSource: {
                        display_id: desktopSource.display_id,
                        id: desktopSource.id,
                        name: desktopSource.name,
                        nonScaledBounds: desktopSource.nonScaledBounds,
                        optionAspectRatio: desktopSource.optionAspectRatio,
                        optionFramerate: desktopSource.optionFramerate,
                        optionWidth: desktopSource.optionWidth,
                        optionHeight: desktopSource.optionHeight,
                        scaleFactor: desktopSource.scaleFactor,
                        screenWidth: desktopSource.screenWidth,
                        screenHeight: desktopSource.screenHeight,
                        screenIndex: desktopSource.screenIndex,
                        screenPositionX: desktopSource.screenPositionX,
                        screenPositionY: desktopSource.screenPositionY,
                    },
                }
                logger.event({ page: 'MediaSelectionResolver', action: 'desktop_selection', data })
            }
        })()
    }, [state.mediaSelection.desktop, appState.appSettings.screenShareDesktopVideoOptions])

    // audioInputs selection changed
    useEffect(
        function audioInputSelectionResolver() {
            async function addDevice(
                addedDevice: AudioInputSelection,
                delta: {
                    audioInputs: AudioTrackWithInfo[]
                    audioInputErrors: AudioInputError[]
                    localLoopbackAudioLivekitTrack?: LocalTrack | null
                }
            ) {
                let addedDeviceSource = userStateRef.current.mediaSources.audioInputs.find(
                    (a) => a.deviceId === addedDevice.deviceId
                )
                if (!addedDeviceSource) {
                    log.error(
                        'audioInputSelectionResolver',
                        `could not add device ${addedDevice.deviceId} because it ` +
                            `does not exist in the UserState::mediaSources.audioInputs list. skipping.`
                    )
                    return delta
                }

                const alreadyExists = !!delta.audioInputs.find((b) => addedDevice.deviceId === b.info.deviceId)
                if (alreadyExists) {
                    log.info(
                        'audioInputSelectionResolver',
                        `skipping (deviceId="${addedDevice.deviceId}" ` +
                            `label="${addedDevice.source.label}") because it already exists in mediaStream.`
                    )
                    return delta
                }

                if (addedDevice.deviceId === '__TEST_ASIO_DEVICE_WITH_WEBRTC_SETTING_mocked_asio_device') {
                    // ASSERT: settings.isAudioEngine should be true.
                    if (addedDevice.settings.isAudioEngine === true) {
                        log.info('', 'TEST_ASIO_DEVICE_WITH_WEBRTC_SETTING: Test passed.')
                    } else {
                        log.assert('', 'TEST_ASIO_DEVICE_WITH_WEBRTC_SETTING: TEST FAILED.')
                    }
                }

                try {
                    let track: AudioTrackWithInfo
                    if (addedDevice.settings.isAudioEngine) {
                        const r = await getAudioEngineInstance().getAudioStream(addedDeviceSource, addedDevice.settings)
                        track = r.track
                        if (Object.keys(r.modifiedSettings).length > 0) {
                            const next = { ...addedDevice.settings, ...r.modifiedSettings }
                            setAudioInputSettings(addedDevice.deviceId, next)
                        }

                        if (
                            addedDevice.source.isSystemAudio &&
                            addedDevice.source.isMuseAudioShare &&
                            addedDevice.settings.enableLocalLoopback
                        ) {
                            delta.localLoopbackAudioLivekitTrack = null
                        }
                    } else {
                        const options = {
                            preventVolumeAutoAdjustment: appStateRef.current.appSettings.preventVolumeAutoAdjustment,
                        }
                        track = await getAudioTrack(addedDeviceSource, addedDevice.settings, options)

                        speakerDetectionManager.registerTrack(track)

                        if (
                            addedDevice.source.isSystemAudio &&
                            addedDevice.source.isMuseAudioShare &&
                            addedDevice.settings.enableLocalLoopback
                        ) {
                            const channelGroup = addedDevice.settings.channelSelection.flat()
                            const channelCt = channelGroup.length || 1

                            delta.localLoopbackAudioLivekitTrack = await createLocalAudioTrack({
                                deviceId: addedDeviceSource.deviceId,
                                echoCancellation: false,
                                noiseSuppression: false,
                                autoGainControl: false,
                                sampleSize: { ideal: 24 },
                                channelCount: Math.max(channelCt, 2),
                                // option introduced in https://github.com/musesessions/muse-livekit-node-client-sdk-js/commit/4be79fc742d726973a71e12f98126ef914b051fe
                                monoChannelSelection: channelCt === 1 ? channelGroup[0] : undefined,
                            })
                        }
                    }

                    delta.audioInputs.push(track)

                    // formerly "log.info(`audioInputSelectionResolver`, `got audio track:`...)
                    log.checkpoint(`resolved_audio_stream`, ``, {
                        id: track.id,
                        label: track.label,
                        source: addedDeviceSource,
                        deviceSettings: addedDevice.settings,
                        capabilities: track.getCapabilities(),
                        settings: track.getSettings(),
                        constraints: track.getConstraints(),
                    })

                    // Register onended event for the track.
                    let onEndedHandler = () => {
                        console.log(
                            `Audio track { id=${track.id} label=${track.label} } onended. Updating mediaSelection.`
                        )
                        dispatch({
                            type: 'update_media_selection.remove',
                            value: { audioInput: addedDevice.deviceId },
                        })

                        const autoRemovedAudioInputsNext = structuredClone(
                            userStateRef.current.mediaSelection.autoRemovedAudioInputs
                        )
                        autoRemovedAudioInputsNext[addedDevice.deviceId] = true
                        dispatch({
                            type: 'update_media_selection',
                            value: { autoRemovedAudioInputs: autoRemovedAudioInputsNext },
                        })
                        logger.event({ page: 'MediaSelectionResolver', action: 'audio_stream_end', data: addedDevice })
                    }
                    track.addEventListener('ended', onEndedHandler)

                    if (track instanceof FakeMediaStreamTrack) {
                        track.addEventListener('onStreamUnresponsive', (stats: AudioEngineLocalStreamStats) => {
                            const next = structuredClone(userStateRef.current.mediaSourceStatus)
                            if (next.audioInputs[addedDevice.deviceId]) {
                                next.audioInputs[addedDevice.deviceId].localStreamStats = stats
                            }

                            dispatch({
                                type: 'update_media_source_status',
                                value: next,
                            })

                            if (shouldReloadDeviceOnUnresponsive(addedDevice.deviceId)) {
                                // reload device
                                reloadAudioDeviceSelection(addedDevice)

                                // save info about the reload
                                saveReloadUnresponsiveDeviceInfo(addedDevice.deviceId)
                            } else {
                                // show dialog: stream unresponsive.
                                appDispatch({
                                    type: 'update_app_state',
                                    value: {
                                        showAudioEngineStreamUnresponsiveDialog: {
                                            selection: addedDevice,
                                            stats: stats,
                                        },
                                    },
                                })
                            }
                        })
                        track.addEventListener('onSampleRateMismatch', (stats: AudioEngineLocalStreamStats) => {
                            const suppressedLabels =
                                appStateRef.current.appSettings.sampleRateMismatchPromptSuppressedLabels
                            if (suppressedLabels[addedDevice.source.displayLabel]) {
                                return
                            }

                            const next = structuredClone(userStateRef.current.mediaSourceStatus)
                            if (next.audioInputs[addedDevice.deviceId]) {
                                next.audioInputs[addedDevice.deviceId].localStreamStats = stats
                            }

                            dispatch({
                                type: 'update_media_source_status',
                                value: next,
                            })

                            // show dialog: sample rate mismatch.
                            appDispatch({
                                type: 'update_app_state',
                                value: {
                                    showAudioEngineSampleRateMismatchDialog: {
                                        selection: addedDevice,
                                        stats: stats,
                                    },
                                },
                            })
                        })
                    }
                } catch (e) {
                    console.error(`Failed to get audio input device stream:`)
                    console.error(e)

                    if (!addedDeviceSource.deviceId) {
                        // don't throw error when deviceId is not defined.
                        // this happens when the user has not granted access to the microphone.
                        return delta
                    }

                    let errorMessage = `Failed to access device "${addedDeviceSource.label}"`
                    if ((e as AudioEngineError)?.debugMessage) {
                        errorMessage += `: ` + (e as AudioEngineError).debugMessage
                    } else if ((e as any)?.message) {
                        errorMessage += `: ` + (e as any).message
                    } else {
                        errorMessage += '.'
                    }

                    let errorData = {
                        message: errorMessage,
                        debugMessage: `code="${(e as any)?.code}" name="${(e as any)?.name}" message="${(e as any)?.message}"`,
                    }

                    delta.audioInputErrors.push(errorData)

                    dispatch({
                        type: 'update_media_selection.remove',
                        value: { audioInput: addedDevice.deviceId },
                    })

                    appDispatch({
                        type: 'update_app_state',
                        value: {
                            showAudioInputDeviceResolutionErrorDialog: errorData,
                        },
                    })

                    // formerly "log.error('resolveAudioSelection', 'failed to access device:', ...)"
                    log.error('e_resolve_audio_stream', '', {
                        device: addedDevice,
                        code: (e as any)?.code,
                        name: (e as any)?.name,
                        message: (e as any)?.message,
                        debugMessage: (e as AudioEngineError)?.debugMessage,
                    })
                }

                return delta
            } // end of addDevice()

            ;(async function () {
                const { added, removed } = audioInputsSelection_debounced_diff
                const audioInputErrors: AudioInputError[] = []

                if (added.length === 0 && removed.length === 0) {
                    return
                }

                console.log(`audioInputs selection changed: added=%O, removed=%O`, added, removed)

                // Create intermediate value that we will be mutating. Make it a new array so that
                // the change will react w/ effects downstream.
                let intermediate = [...userStateRef.current.mediaStreams.audioInputs]

                // For removed devices, find the matching stream in state.mediaStreams and remove them.
                for (let removedDevice of removed) {
                    intermediate
                        .filter((track) => track.info.deviceId === removedDevice.deviceId)
                        .forEach((track) => {
                            track.stop()
                            speakerDetectionManager.unregisterTrack(track)
                        })
                    intermediate = intermediate.filter((track) => track.info.deviceId !== removedDevice.deviceId)
                }

                let mediaStreamsDelta: {
                    audioInputs: AudioTrackWithInfo[]
                    audioInputErrors: AudioInputError[]
                    localLoopbackAudioLivekitTrack?: LocalTrack | null
                } = {
                    audioInputs: intermediate,
                    audioInputErrors: [],
                }

                // For removed devices, clear `localStreamStats` in `mediaSourceStatus.audioInputs`.
                if (removed.length > 0) {
                    let audioInputsNext = structuredClone(state.mediaSourceStatus.audioInputs)
                    for (let removedDevice of removed) {
                        if (audioInputsNext[removedDevice.deviceId]) {
                            audioInputsNext[removedDevice.deviceId].localStreamStats = null
                        }

                        if (removedDevice.source.isSystemAudio && removedDevice.settings.enableLocalLoopback) {
                            mediaStreamsDelta.localLoopbackAudioLivekitTrack = null
                        }
                    }

                    dispatch({
                        type: 'update_media_source_status',
                        value: { audioInputs: audioInputsNext },
                    })
                }

                // For added devices:
                for (let addedDevice of added) {
                    mediaStreamsDelta = await addDevice(addedDevice, mediaStreamsDelta)
                }

                // update UserState.mediaStreams
                dispatch({
                    type: 'update_media_streams',
                    value: mediaStreamsDelta,
                })

                // NOTE: null is also a valid value
                if (mediaStreamsDelta.localLoopbackAudioLivekitTrack !== undefined) {
                    roomDispatch({
                        type: 'update_room_context',
                        value: { localLoopbackAudioLivekitTrack: mediaStreamsDelta.localLoopbackAudioLivekitTrack },
                    })
                }
            })()
        },
        [audioInputsSelection_debounced]
    )

    const { setAudioOutputDevice } = useAudioEngineActions()
    useEffect(
        function audioOutputSelectionResolver() {
            // NOTE: This prevents setting output to null, which will prevent pro
            // audio from working for listenonly clients.
            if (!userStateRef.current.mediaSources.loaded) {
                return
            }

            const source = userStateRef.current.mediaSources.audioOutputs.find(
                (a) => a.deviceId === state.mediaSelection.audioOutput
            )

            log.checkpoint(`set_audio_output_device`, ``, { source })

            if (source && source.hostApiName === 'ASIO') {
                appDispatch({
                    type: 'new_message_dialog',
                    value: {
                        id: 'asio_outputs_not_supported_error',
                        text: 'Speaker ASIO devices are not supported at this time.\nPlease select a different speaker device.',
                        negativeButtonDoNotShowAgain: true,
                    },
                })
                return
            }

            setAudioOutputDevice(state.mediaSelection.audioOutput)
        },
        [state.mediaSelection.audioOutput]
    )

    // Store mediaSelection configuration on change so that we can use that as the default later.
    // It is used in `chooseInitialMediaSelectionDefaults()`.
    // We store the label since the ids change on every instance of the app.
    useEffect(() => {
        const mediaSelection = state.mediaSelection
        const audioOutputSelectionDevice =
            state.mediaSources.audioOutputs.find((a) => a.deviceId === mediaSelection.audioOutput) || null
        const audioOutputMuseRecordingSelectionDevice =
            state.mediaSources.audioOutputsAll.find((a) => a.deviceId === mediaSelection.audioOutputMuseRecording) ||
            null
        const videoSelectionDevice = state.mediaSources.video.find((a) => a.deviceId === mediaSelection.video) || null
        const midiOutputDevice = state.mediaSources.midiOutputs.find((a) => a.id === mediaSelection.midiOutput) || null
        const midiOutputPassthroughDevice =
            state.mediaSources.midiOutputs.find((a) => a.id === mediaSelection.midiPassthroughOutput) || null

        if (
            audioOutputSelectionDevice == null &&
            videoSelectionDevice == null &&
            state.mediaSelection.audioInputs.length === 0
        ) {
            // Then, it's a mediaSelection cleared from the app, not the user. Don't save.
        } else {
            let talkbackMic: string = '_none'
            if (state.mediaSelection.talkbackMic !== '_none') {
                talkbackMic =
                    state.mediaSources.audioInputs.find((a) => a.deviceId === state.mediaSelection.talkbackMic)
                        ?.label || '_undef'
            }

            const audioInputsNext = state.mediaSelection.audioInputs
                .filter((a) => !a.source.isMuseDAWBridge)
                .filter((a) => !a.source.isMuseAudioShare)
                .map((a) => a.source.label)

            persistent.set(media_selection_prev_preference, {
                video: videoSelectionDevice ? videoSelectionDevice.label : null,
                audioInputs: audioInputsNext,
                talkbackMic: talkbackMic,
                audioOutput: audioOutputSelectionDevice ? audioOutputSelectionDevice.label : null,
                audioOutputMuseRecording: audioOutputMuseRecordingSelectionDevice
                    ? audioOutputMuseRecordingSelectionDevice.label
                    : null,
                midiOutputDevice: midiOutputDevice ? midiOutputDevice.name : null,
                midiOutputPassthroughDevice: midiOutputPassthroughDevice ? midiOutputPassthroughDevice.name : null,
            } as MediaSelectionPrevPreference)
        }
    }, [state.mediaSources, state.mediaSelection])

    useEffect(() => {
        return () => {
            dispatch({
                type: 'clear_media_selection_and_streams',
                value: null,
            })
        }
    }, [])

    return <></>
}

export default MediaSelectionResolver
